<template>
  <v-container
    v-resize="updateWindowSize"
    fluid
    fill-height
    class="bm-page1"
    pa-0
  >
    <v-layout column fill-height>
      <v-flex flex-main-scroll>
        <v-container fluid pa-0 class="full-height">
          <v-layout column class="full-height">
            <v-flex
              no-flex
              :class="{
                'bm-pagehead': true,
                'bm-grid-controls': hasGridControls,
              }"
              pa-3
              :pa-6="$vuetify.breakpoint.smAndUp"
            >
              <v-layout wrap justify-end align-center>
                <v-flex no-flex>
                  <slot name="breadcrumbs">
                    <bm-breadcrumbs
                      :parent-route="parentRoute"
                      :parent-route-location="parentRouteLocation"
                    />
                  </slot>
                  <slot name="title">
                    <h1 v-if="parentRouteLocation" data-qa="page_title">
                      {{ title }}
                      <router-link
                        :to="parentRouteLocation"
                        :target="openLocationInNewTab ? '_blank' : '_self'"
                      >
                        {{ titleWithRoute }}
                      </router-link>
                      <v-progress-circular
                        v-if="isLoading && !$slots['subtitle']"
                        size="28"
                        width="3"
                        class="my-auto"
                        data-qa="page-lazy-loader"
                        indeterminate
                      />
                    </h1>
                    <h1 v-else data-qa="page_title">
                      {{ title }}
                      <v-progress-circular
                        v-if="isLoading && !$slots['subtitle']"
                        size="28"
                        width="3"
                        class="my-auto"
                        data-qa="page-lazy-loader"
                        indeterminate
                      />
                    </h1>
                  </slot>
                  <div class="d-flex">
                    <slot name="subtitle">
                      <div v-if="subtitle" data-qa="page_subtitle">
                        <span v-if="subtitleLocation">
                          <router-link
                            :to="subtitleLocation"
                            :target="openLocationInNewTab ? '_blank' : '_self'"
                          >
                            {{ subtitle }}
                          </router-link>
                        </span>
                        <span v-else>
                          {{ subtitle }}
                        </span>
                      </div>
                    </slot>
                    <v-progress-circular
                      v-if="isLoading && $slots['subtitle']"
                      size="14"
                      width="2"
                      class="ml-2 my-auto"
                      indeterminate
                    />
                  </div>
                </v-flex>
                <v-spacer />
                <v-flex :shrink="shrinkStatus" class="bm-heading2 pt-2">
                  <slot name="status">
                    <div v-if="status" data-qa="page_status">
                      {{ status }}
                    </div>
                  </slot>
                  <slot name="grid-controls">
                    <portal-target name="grid-controls" />
                  </slot>
                </v-flex>
              </v-layout>
              <v-divider class="bm-grid-divider" />
            </v-flex>
            <v-flex
              :px-3="!removePagePadding"
              :px-6="$vuetify.breakpoint.smAndUp && !removePagePadding"
              class="grid-page-content"
            >
              <slot />
            </v-flex>
          </v-layout>
        </v-container>
      </v-flex>
    </v-layout>
  </v-container>
</template>

<script lang="ts">
import BmBreadcrumbs from "@/components/controls/app/BmBreadcrumbs.vue";
import appStore from "@/store/modules/appStore";
import type { PropType } from "vue";
import { computed, defineComponent, onMounted, ref, watch } from "vue";
import type { Location } from "vue-router";
import { Wormhole } from "portal-vue";
import { Resize } from "vuetify/lib";
import debounce from "lodash-es/debounce";

export default defineComponent({
  name: "BmPage",
  components: { BmBreadcrumbs },
  directives: { Resize },
  props: {
    title: {
      type: String,
      default: "",
    },
    subtitle: {
      type: String,
      default: "",
    },
    subtitleLocation: {
      type: Object as PropType<Location | null>,
      required: false,
      default: () => null,
    },
    openLocationInNewTab: {
      type: Boolean,
      default: false,
    },
    status: {
      type: String,
      default: "",
    },
    parentRoute: {
      type: String as PropType<string | null>,
      default: null,
    },
    removePagePadding: {
      type: Boolean,
      default: false,
    },
    isLoading: {
      type: Boolean,
      required: false,
      default: false,
    },
    shrinkStatus: {
      type: Boolean,
      required: false,
      default: true,
    },
    parentRouteLocation: {
      type: Object as PropType<Location | null>,
      required: false,
      default: () => null,
    },
    titleWithRoute: {
      type: String,
      default: "",
    },
  },
  emits: ["content-height-resized"],
  setup(props, context) {
    const currentWindowSize = ref<number | null>(null);

    const hasGridControls = computed(() => {
      return (
        context.slots["grid-controls"] !== undefined ||
        Wormhole.hasTarget("grid-controls")
      );
    });

    const availableContentHeight = computed(() => {
      if (!currentWindowSize.value) {
        return undefined;
      }

      const pageHeaderHeight =
        document.querySelector(".bm-grid-controls")?.getBoundingClientRect()
          .height ?? 146;

      const gridPadding = 10;
      return currentWindowSize.value - pageHeaderHeight - gridPadding;
    });

    watch(
      () => props.title,
      (title) => {
        appStore.setDocumentTitle(title);
      },
      { immediate: true }
    );

    const updateWindowSize = debounce(() => {
      currentWindowSize.value =
        document.querySelector(".bm-page1")?.getBoundingClientRect().height ??
        null;

      context.emit("content-height-resized", availableContentHeight.value);
    }, 300);

    onMounted(() => {
      updateWindowSize();
    });

    return {
      currentWindowSize,
      hasGridControls,
      availableContentHeight,
      updateWindowSize,
    };
  },
});
</script>

<style scoped lang="sass">
.fit-content
  min-width: fit-content

// top and left/right padding to match vue container
.bm-pagehead
  padding-top: 12px
  padding-left: 12px
  padding-right: 12px

.bm-pagehead:not(.bm-grid-controls)
  margin-bottom: 20px

// Move data filters closer to the header as they have a lot of whitespace at the top.
.bm-grid-data-filter
  margin-top: -20px

.bm-grid-divider
  margin-bottom: 10px

.flex-main-scroll
  min-height: 300px
  flex: 1 1 0
  overflow: auto

.no-flex
  flex: 0 0 auto

.grid-page-content
  display: flex
  flex-flow: column

@media print
// reduce padding on print
 .grid-page-content
    padding: 0 !important
</style>
